<template>
  <section
    ref="track"
    class="svc-sticky"
    :class="{ 'is-static': isStatic }"
    :aria-label="$t('SectionThree.title')"
  >
    <!-- Deep-link targets (Footer, ?scrollTo=). Positioned at each service's resting scroll offset. -->
    <span
      v-for="(service, index) in services"
      :id="service.anchor"
      :key="`anchor-${service.anchor}`"
      ref="anchors"
      class="svc-sticky__anchor"
      :style="{ '--svc-index': index }"
    ></span>

    <div ref="stage" class="svc-sticky__stage">
      <article
        v-for="(service, index) in services"
        :key="service.anchor"
        ref="panels"
        class="svc-panel"
        :style="{ zIndex: index + 1 }"
      >
        <div class="svc-panel__media">
          <img
            ref="images"
            class="svc-panel__image"
            :src="service.image"
            alt=""
            loading="lazy"
            decoding="async"
          />
        </div>
        <div class="svc-panel__overlay"></div>
        <div ref="shades" class="svc-panel__shade"></div>

        <div ref="inners" class="svc-panel__inner">
          <div ref="contents" class="svc-panel__content">
            <p class="svc-panel__index">{{ pad(index + 1) }} / {{ pad(services.length) }}</p>
            <h3 ref="titles" class="svc-panel__title">
              <span class="svc-panel__kicker">{{ service.title }}</span>
              <span class="svc-panel__headline">{{ service.title2 }}</span>
            </h3>
            <p ref="descriptions" class="svc-panel__description">{{ service.description }}</p>
          </div>
        </div>
      </article>

      <div class="svc-sticky__hud" aria-hidden="true">
        <div class="svc-sticky__progress">
          <span class="svc-sticky__counter">
            <span class="svc-sticky__current">
              <span
                v-for="(service, index) in services"
                :key="`num-${service.anchor}`"
                ref="numbers"
                class="svc-sticky__number"
                >{{ pad(index + 1) }}</span
              >
            </span>
            <span class="svc-sticky__total">/ {{ pad(services.length) }}</span>
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// Timeline units, measured in viewport heights of scroll (keep in sync with _services-sticky.scss).
const HOLD = 0.5 // reading time per service before the next one starts rising
const TRANSITION = 1 // the next panel travels one full viewport
const TEXT_SHIFT = 32

export default {
  name: 'ServicesSticky',
  data() {
    return {
      isStatic: false
    }
  },
  computed: {
    services() {
      // Order matches the previous Homepage layout; anchors are used by Footer links.
      return [
        {
          key: 'onedata',
          anchor: 'service-clearing-house',
          image: require('@/assets/images/services/clearing-house.webp')
        },
        {
          key: 'threedata',
          anchor: 'service-consultancy',
          image: require('@/assets/images/services/smart-grids.webp')
        },
        {
          key: 'twodata',
          anchor: 'service-ev-roaming',
          image: require('@/assets/images/services/api-integration.webp')
        },
        {
          key: 'fourdata',
          anchor: 'service-platform-development',
          image: require('@/assets/images/services/platform-development.webp')
        }
      ].map((service) => ({
        ...service,
        title: this.$t(`SectionThree.${service.key}.title`),
        title2: this.$t(`SectionThree.${service.key}.title2`),
        description: this.$t(`SectionThree.${service.key}.description`)
      }))
    }
  },
  watch: {
    '$i18n.locale'() {
      this.$nextTick(() => this.evaluateMode())
    }
  },
  mounted() {
    this.ctx = null
    this.resizeTimer = null
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.onMotionChange = () => this.evaluateMode()
    if (this.reducedMotion.addEventListener) {
      this.reducedMotion.addEventListener('change', this.onMotionChange)
    } else {
      this.reducedMotion.addListener(this.onMotionChange)
    }

    // ScrollTrigger.refresh() cancels an in-flight smooth scroll (Footer / ?scrollTo= links),
    // so layout re-checks wait until scrolling has been idle for a moment.
    this.lastScrollAt = 0
    this.onScroll = () => {
      this.lastScrollAt = Date.now()
    }
    window.addEventListener('scroll', this.onScroll, { passive: true })

    this.onResize = () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (Date.now() - this.lastScrollAt < 250) this.onResize()
        else this.evaluateMode()
      }, 150)
    }
    window.addEventListener('resize', this.onResize, { passive: true })

    // Content above (images, AOS, fonts) can shift the section after mount.
    if (window.ResizeObserver) {
      this.bodyHeight = 0
      this.pageObserver = new ResizeObserver(([entry]) => {
        const height = Math.round(entry.contentRect.height)
        if (height === this.bodyHeight) return
        this.bodyHeight = height
        this.onResize()
      })
      this.pageObserver.observe(document.body)
    }

    // Stacked sheets sit clipped below the fold, so native lazy-loading would only fetch them
    // mid-transition. Start all of them once the section is about a viewport away.
    if (window.IntersectionObserver) {
      this.imageObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return
          this.$refs.images.forEach((image) => image.setAttribute('loading', 'eager'))
          this.imageObserver.disconnect()
        },
        { rootMargin: '100% 0px' }
      )
      this.imageObserver.observe(this.$refs.track)
    }

    this.evaluateMode(true)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!this._isDestroyed) this.onResize()
      })
    }
  },
  beforeDestroy() {
    clearTimeout(this.resizeTimer)
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('scroll', this.onScroll)
    if (this.reducedMotion.removeEventListener) {
      this.reducedMotion.removeEventListener('change', this.onMotionChange)
    } else {
      this.reducedMotion.removeListener(this.onMotionChange)
    }
    if (this.pageObserver) this.pageObserver.disconnect()
    if (this.imageObserver) this.imageObserver.disconnect()
    this.teardown()
  },
  methods: {
    pad(value) {
      return String(value).padStart(2, '0')
    },
    contentFits() {
      const viewport = window.innerHeight
      if (viewport < 500) return false
      const { inners, titles, descriptions } = this.$refs
      // Title + description only: the static-mode index label must not count, or the mode would stick.
      return inners.every((inner, index) => {
        const style = window.getComputedStyle(inner)
        const title = titles[index]
        const needed =
          parseFloat(style.paddingTop) +
          parseFloat(style.paddingBottom) +
          title.offsetHeight +
          parseFloat(window.getComputedStyle(title).marginBottom) +
          descriptions[index].offsetHeight
        return needed <= viewport
      })
    },
    evaluateMode(force = false) {
      // Without overflow: clip, <body> falls back to overflow-x: hidden and sticky cannot stick.
      const canStick = window.CSS && CSS.supports('overflow', 'clip')
      const shouldBeStatic = !canStick || this.reducedMotion.matches || !this.contentFits()
      if (!force && shouldBeStatic === this.isStatic) {
        this.positionAnchors()
        ScrollTrigger.refresh()
        return
      }
      this.teardown()
      this.isStatic = shouldBeStatic
      this.$nextTick(() => {
        if (!this.isStatic) this.build()
        this.positionAnchors()
        ScrollTrigger.refresh()
      })
    },
    positionAnchors() {
      const { anchors, panels } = this.$refs
      anchors.forEach((anchor, index) => {
        anchor.style.top = this.isStatic ? `${panels[index].offsetTop}px` : ''
      })
    },
    build() {
      const { track, panels, images, shades, titles, descriptions, numbers } = this.$refs
      const count = panels.length

      this.ctx = gsap.context(() => {
        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: track,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 0.4,
            invalidateOnRefresh: true
          }
        })

        gsap.set(panels.slice(1), { yPercent: 100 })
        gsap.set(images, { scale: 1.03, transformOrigin: '50% 50%' })
        gsap.set([titles.slice(1), descriptions.slice(1)], { y: TEXT_SHIFT, opacity: 0 })
        gsap.set(numbers.slice(1), { yPercent: 100, opacity: 0 })

        // The first image settles while its panel is held.
        tl.to(images[0], { scale: 1, duration: HOLD }, 0)

        for (let next = 1; next < count; next++) {
          const prev = next - 1
          const start = next * HOLD + prev * TRANSITION

          // Next sheet rises over the held one; the held sheet never moves, it only dims.
          tl.to(panels[next], { yPercent: 0, duration: TRANSITION }, start)
          tl.to(images[next], { scale: 1, duration: TRANSITION }, start)
          tl.fromTo(shades[prev], { opacity: 0 }, { opacity: 0.55, duration: TRANSITION }, start)

          // Outgoing copy lifts away before the rising sheet reaches it.
          tl.to(
            titles[prev],
            { y: -TEXT_SHIFT, opacity: 0, duration: 0.3, ease: 'power1.in' },
            start
          )
          tl.to(
            descriptions[prev],
            { y: -TEXT_SHIFT, opacity: 0, duration: 0.3, ease: 'power1.in' },
            start + 0.05
          )

          // Incoming copy settles once its sheet is mostly in place.
          tl.to(
            titles[next],
            { y: 0, opacity: 1, duration: 0.38, ease: 'power2.out' },
            start + 0.55
          )
          tl.to(
            descriptions[next],
            { y: 0, opacity: 1, duration: 0.38, ease: 'power2.out' },
            start + 0.62
          )

          tl.to(
            numbers[prev],
            { yPercent: -100, opacity: 0, duration: 0.2, ease: 'power1.in' },
            start + 0.4
          )
          tl.to(
            numbers[next],
            { yPercent: 0, opacity: 1, duration: 0.2, ease: 'power1.out' },
            start + 0.5
          )
        }

        // Final hold before the section releases.
        tl.set({}, {}, count * HOLD + (count - 1) * TRANSITION)
      }, this.$el)
    },
    teardown() {
      if (this.ctx) {
        this.ctx.revert()
        this.ctx = null
      }
    }
  }
}
</script>
